function logMap(t, min, max) {
  const clamped = Math.min(1, Math.max(0, Number(t) / 100));
  return Math.round(Math.exp(Math.log(min) + clamped * (Math.log(max) - Math.log(min))));
}

function money(n) {
  const value = Math.max(0, Math.round(n));
  return `$${value.toLocaleString("en-US")}`;
}

function pickEdition(input, rates) {
  if (input.pilot) return "evaluate";
  if (input.deploy === "sovereign") return "sovereign";
  if (
    input.messages >= rates.thresholds.sovereignMessages ||
    input.mau >= 120000
  ) {
    return "sovereign";
  }
  if (
    input.deploy === "hybrid" ||
    input.channels > 0 ||
    input.messages >= rates.thresholds.compileMessages ||
    input.mau >= rates.thresholds.compileMau ||
    input.surfaces > 2 ||
    input.streams > 10
  ) {
    return "compile";
  }
  return "assemble";
}

function extraPacks(needed, included, packSize) {
  const gap = Math.max(0, needed - included);
  if (gap === 0) return 0;
  return Math.ceil(gap / packSize);
}

const why = {
  evaluate: "Pilot flag is on. Forty-five day sandbox; the fee credits toward Assemble, Compile, or Sovereign if you convert in 90 days.",
  assemble: "Managed runtime, modest volume, web surfaces, no channel connectors. Assemble is the first production contract.",
  compile:
    "Volume, extra surfaces, channels, hybrid, or streaming concurrency above Assemble. Compile is the usual production record.",
  sovereign:
    "Self-host, air-gap, or traffic past the Compile band. Software license plus optional Restora-operated nodes.",
};

export function initPricingCalculator() {
  const root = document.querySelector("[data-pricing-calc]");
  const rateNode = document.getElementById("essenize-pricing-rates");
  if (!root || !rateNode) return;

  const rates = JSON.parse(rateNode.textContent);
  const form = root.querySelector("#pricing-calc-form");
  const billTabs = document.querySelectorAll(".bill-toggle [data-bill]");

  const syncCards = (billing) => {
    document.querySelectorAll(".price-card[data-edition]").forEach((card) => {
      const amount = card.querySelector("[data-amount]");
      const cadence = card.querySelector("[data-cadence]");
      if (!amount) return;
      const monthly = card.dataset.monthly;
      const annual = card.dataset.annual;
      if (card.dataset.edition === "evaluate") {
        amount.textContent = `$${monthly}`;
        if (cadence) cadence.textContent = "one-time";
        return;
      }
      amount.textContent = billing === "annual" ? `$${annual}` : `$${monthly}`;
      if (cadence) {
        cadence.textContent = billing === "annual" ? "per month, annual commit" : "per month, monthly list";
      }
    });
  };

  billTabs.forEach((tab) => {
    tab.addEventListener("click", () => {
      billTabs.forEach((item) => item.classList.toggle("is-active", item === tab));
      const billing = tab.dataset.bill;
      const radio = form.querySelector(`input[name="billing"][value="${billing}"]`);
      if (radio) radio.checked = true;
      syncCards(billing);
      render();
    });
  });

  const read = () => {
    const data = new FormData(form);
    const channels = form.querySelectorAll('input[name="ch"]:checked').length;
    return {
      deploy: data.get("deploy"),
      billing: data.get("billing"),
      messages: logMap(data.get("messages"), 10000, 40000000),
      mau: logMap(data.get("mau"), 200, 1600000),
      surfaces: Number(data.get("surfaces")),
      channels,
      streams: Number(data.get("streams")),
      analytics: Number(data.get("analytics")),
      support: data.get("support"),
      operate: data.has("operate"),
      nodes: Number(data.get("nodes")),
      routing: data.get("routing"),
      modelSpend: Number(data.get("modelSpend") || 0),
      pilot: data.has("pilot"),
    };
  };

  const render = () => {
    const input = read();
    document.getElementById("calc-messages-label").textContent = input.messages.toLocaleString("en-US");
    document.getElementById("calc-mau-label").textContent = input.mau.toLocaleString("en-US");
    document.getElementById("calc-surfaces-label").textContent = String(input.surfaces);
    document.getElementById("calc-streams-label").textContent = String(input.streams);
    document.getElementById("calc-nodes-label").textContent = String(input.nodes);

    const nodesField = form.querySelector("[data-nodes-field]");
    const modelField = form.querySelector("[data-model-field]");
    if (nodesField) nodesField.hidden = !input.operate;
    if (modelField) modelField.hidden = input.routing !== "restora";

    const editionId = pickEdition(input, rates);
    const edition = rates.editions[editionId];
    const lines = [];
    let monthly = 0;

    const kicker = root.querySelector(".calc-result .kicker");
    if (edition.once) {
      monthly = edition.base;
      if (kicker) kicker.textContent = "Indicative pilot fee";
      lines.push(["Evaluate (45-day sandbox)", money(monthly)]);
    } else {
      if (kicker) kicker.textContent = "Indicative monthly";
      const base = input.billing === "annual" ? edition.baseAnnual : edition.baseMonthly;
      monthly += base;
      lines.push([`${editionId[0].toUpperCase()}${editionId.slice(1)} platform`, money(base)]);

      const msgOverage = Math.max(0, input.messages - edition.messages) * edition.msgRate;
      if (msgOverage > 0) {
        monthly += msgOverage;
        lines.push(["Message overage", money(msgOverage)]);
      }

      const mauOverage = Math.max(0, input.mau - edition.mau) * edition.mauRate;
      if (mauOverage > 0) {
        monthly += mauOverage;
        lines.push(["MAU overage", money(mauOverage)]);
      }

      const extraSurfaces = Math.max(0, input.surfaces - edition.surfaces);
      if (extraSurfaces > 0) {
        const cost = extraSurfaces * rates.add.surface;
        monthly += cost;
        lines.push([`${extraSurfaces} extra surface${extraSurfaces > 1 ? "s" : ""}`, money(cost)]);
      }

      const extraChannels = Math.max(0, input.channels - edition.channels);
      if (extraChannels > 0) {
        const cost = extraChannels * rates.add.channel;
        monthly += cost;
        lines.push([`${extraChannels} channel connector${extraChannels > 1 ? "s" : ""}`, money(cost)]);
      }

      const packs = extraPacks(input.streams, edition.streams, rates.add.streamPackSize);
      if (packs > 0) {
        const cost = packs * rates.add.streamPack;
        monthly += cost;
        lines.push([`${packs} streaming pack${packs > 1 ? "s" : ""}`, money(cost)]);
      }

      if (input.analytics >= 365 && edition.analyticsDays < 365) {
        monthly += rates.add.analytics365;
        lines.push(["13-month analytics retention", money(rates.add.analytics365)]);
      } else if (input.analytics >= 90 && edition.analyticsDays < 90) {
        monthly += rates.add.analytics90;
        lines.push(["90-day analytics retention", money(rates.add.analytics90)]);
      }

      if (input.support === "priority") {
        monthly += rates.add.supportPriority;
        lines.push(["Priority support", money(rates.add.supportPriority)]);
      } else if (input.support === "sun") {
        monthly += rates.add.supportSun;
        lines.push(["Follow-the-sun", money(rates.add.supportSun)]);
      }

      if (input.operate) {
        const cost = input.nodes * rates.add.infraNode;
        monthly += cost;
        lines.push([`${input.nodes} Restora-operated nodes`, money(cost)]);
      }

      if (input.routing === "restora" && input.modelSpend > 0) {
        const cost = input.modelSpend * (1 + rates.add.routingPct);
        monthly += cost;
        lines.push(["Restora-routed models (provider + 12%)", money(cost)]);
      }
    }

    root.querySelector("[data-calc-edition]").textContent = editionId[0].toUpperCase() + editionId.slice(1);
    root.querySelector("[data-calc-why]").textContent = why[editionId];
    root.querySelector("[data-calc-total]").textContent = money(monthly);
    const annualNote = root.querySelector("[data-calc-annual]");
    if (edition.once) {
      annualNote.textContent = `${money(rates.editions.evaluate.base)} one-time. Credits toward Assemble, Compile, or Sovereign within 90 days.`;
    } else {
      annualNote.textContent =
        input.billing === "annual"
          ? `${money(monthly * 12)} annual at commit rates (unused included volume does not roll).`
          : `${money(monthly * 12)} if you stayed on monthly list for a year. Annual commit lowers the platform line.`;
    }

    const list = root.querySelector("[data-calc-lines]");
    list.innerHTML = lines
      .map(([label, value]) => `<li><span>${label}</span><strong>${value}</strong></li>`)
      .join("");

    const excludes = [];
    if (input.routing !== "restora") excludes.push("your model invoice");
    if (input.channels > 0) excludes.push("WhatsApp / Slack / Discord network fees");
    if (!input.operate && input.deploy === "sovereign") excludes.push("your cluster");
    root.querySelector("[data-calc-excludes]").textContent = excludes.length
      ? `Not in this number: ${excludes.join("; ")}.`
      : "Model pass-through and customer cloud stay off this number.";

    const cta = root.querySelector("[data-calc-cta]");
    const params = new URLSearchParams({
      interest: "Volume record / pricing",
      edition: editionId,
      messages: String(input.messages),
      mau: String(input.mau),
    });
    cta.href = `/contact/?${params.toString()}`;
  };

  form.addEventListener("input", render);
  form.addEventListener("change", render);
  syncCards("monthly");
  render();
}
